import type { DebtData, PaymentConfiguration } from '@/types';
import type { OphelosPaymentPlanResponse } from '@/types/payment-plan';
import { coveredByAccountPayment } from './accountPayment';
import { accountPaymentOffer } from './accountPaymentOffer';
import { type DebtPage, debtPage } from './debtNavigation';
import { activeAccountPayment, isScheduledPayment } from './paymentPlans';
import { isPaymentTab, type PaymentTab, paymentTabsOnOffer, resolveActivePaymentTab } from './paymentTabs';

/**
 * The livechat agent offers a page by calling a tool that names it; the chat turns the call
 * into a button. The agent never writes a URL: the page, the debt's id and the tab are all
 * resolved here, from the portal's own state and routing.
 */

// Mirrored, with the tool name, its argument names and the session-state keys of
// offerablePages, in src/python/services/agentos/src/services/agentos/tools/portal_navigation_tools.py
// (PAYMENT_TABS lives in ./paymentTabs).
export const PORTAL_PAGE_TOOL_NAME = 'show_portal_page';
export const PORTAL_PAGES = ['payment', 'account_payment', 'payment_plan', 'payment_history', 'budget_assessment'] as const;
export const OFFERED_PREFIX = 'Offered:';

export type PortalPage = (typeof PORTAL_PAGES)[number];

/** A page as the agent asked for it: a debt by the reference the customer knows, never its id. */
export interface PortalPageRequest {
  page: PortalPage;
  debtRef?: string;
  tab?: PaymentTab;
}

export interface PortalPageLink {
  to: string;
  kind: DebtPage['kind'] | 'account_payment' | 'payment_history' | 'budget_assessment';
  /** The debt a debt page opens, so its button can name it. */
  debt?: DebtData;
  /** The tab a payment page opens on, so a debt's button quotes that tab's price. None while a debt's payment is scheduled. */
  tab?: PaymentTab;
}

/** The portal's state a link is resolved from. */
export interface PortalLinkState {
  debts: DebtData[];
  accountPaymentPlan: OphelosPaymentPlanResponse | null;
  /** Whether the budget assessment page would open, rather than send the customer back. */
  budgetAssessmentOpen: boolean;
  /** The operator's payment config, which decides the tabs a debt's payment page offers. */
  payment: PaymentConfiguration;
}

interface ToolCallEventData {
  tool?: { tool_name?: unknown; tool_args?: unknown; result?: unknown; tool_call_error?: unknown } | null;
}

const isPortalPage = (value: unknown): value is PortalPage => (PORTAL_PAGES as readonly unknown[]).includes(value);

/**
 * The page an AgentOS `ToolCallCompleted` event offers, or null for any other event.
 *
 * Only a call the tool itself offered counts: its refusals (a reference that is not one debt,
 * a page this portal cannot open) are what the agent's reply follows, so a button beside them
 * would contradict the text.
 */
export function portalPageRequestFromEvent(eventName: string, data: unknown): PortalPageRequest | null {
  if (eventName !== 'ToolCallCompleted') return null;
  const tool = (data as ToolCallEventData | null | undefined)?.tool;
  if (tool?.tool_name !== PORTAL_PAGE_TOOL_NAME || tool.tool_call_error) return null;
  if (typeof tool.result !== 'string' || !tool.result.startsWith(OFFERED_PREFIX)) return null;

  const args = tool.tool_args;
  if (!args || typeof args !== 'object') return null;
  const { page, debt_ref: rawRef, tab } = args as Record<string, unknown>;
  if (!isPortalPage(page) || (tab != null && !isPaymentTab(tab))) return null;

  const debtRef = typeof rawRef === 'string' ? rawRef.trim() : '';
  return { page, ...(debtRef && { debtRef }), ...(tab != null && { tab }) };
}

const withTab = (path: string, tab: PaymentTab | undefined) => (tab ? `${path}?tab=${tab}` : path);

/**
 * Where the button to a requested page goes, decided the way the portal decides it elsewhere:
 * a debt opens where the dashboard opens it, and Pay all only where the account page is open,
 * each on the tab its payment page would show.
 * Null when there is no page to offer, and for every page while the state is still loading.
 */
export function portalPageLink(request: PortalPageRequest, state: PortalLinkState | null): PortalPageLink | null {
  if (!state) return null;
  const { debts, accountPaymentPlan, budgetAssessmentOpen, payment } = state;
  switch (request.page) {
    case 'payment_history':
      return { to: '/payment-history', kind: 'payment_history' };
    case 'budget_assessment':
      return budgetAssessmentOpen ? { to: '/budget-assessment', kind: 'budget_assessment' } : null;
    case 'account_payment': {
      // A blocked page shows only its reason, so it is not offered.
      const offer = accountPaymentOffer({ debts, accountPaymentPlan, payment });
      if (offer.surface !== 'open') return null;
      const tab = resolveActivePaymentTab(request.tab ?? 'full', offer.tabs);
      return { to: withTab('/account-payment', tab), kind: 'account_payment', tab };
    }
    case 'payment':
    case 'payment_plan': {
      const matches = request.debtRef ? debts.filter((d) => d.externalCaseRef?.trim() === request.debtRef) : [];
      const debt = matches.length === 1 ? matches[0] : null;
      const accountPayment = activeAccountPayment(accountPaymentPlan);
      const page = debt && debtPage(debt, accountPayment);
      if (!debt || !page) return null;
      // Only the payment page takes a tab.
      const asked = request.page === 'payment_plan' ? 'plan' : request.tab;
      if (page.kind !== 'payment') return { kind: page.kind, to: page.path, debt };
      if (coveredByAccountPayment(debt, accountPayment)) return { kind: 'payment', to: withTab(page.path, asked), debt };
      // A debt with a payment scheduled shows that payment in place of the tabs.
      if (isScheduledPayment(debt.paymentPlan)) return { kind: 'payment', to: page.path, debt };
      // The debt's own page falls back from a tab the operator does not offer, so name the tab it opens on.
      const tab = resolveActivePaymentTab(asked ?? 'full', paymentTabsOnOffer(debt.currentBalance, payment));
      return { kind: 'payment', to: withTab(page.path, tab), debt, tab };
    }
  }
}

/**
 * What the portal can open right now, sent with every chat run: the tool offers only these, so
 * the agent never promises a button this portal would not draw. A debt page needs no entry of
 * its own, since `portalPageLink` gives none without a reference.
 */
export const offerablePages = (state: PortalLinkState | null) => ({
  portal_pages: PORTAL_PAGES.filter((page) => portalPageLink({ page }, state)),
  portal_debt_refs: [...new Set((state?.debts ?? []).flatMap(({ externalCaseRef }) => externalCaseRef?.trim() || []))].filter((debtRef) =>
    portalPageLink({ page: 'payment', debtRef }, state),
  ),
});

// Mirrored in src/python/services/agentos/src/services/agentos/hooks/customer_data_hook.py.
export const ACCOUNT_DIGEST_KEY = 'account_digest';

/**
 * The account as this portal shows it, sent with every chat run: the agent loads the account
 * once and reloads it whenever this changes, so a reply after the customer cancels a plan or a
 * scheduled payment beside the chat describes the account as it now is. Null while the state is
 * still loading.
 */
export function accountDigest(state: PortalLinkState | null): string | null {
  if (!state) return null;
  const { debts, accountPaymentPlan: account } = state;
  return JSON.stringify([
    account && [account.id, account.status, account.is_scheduled_payment],
    [...debts]
      .sort((a, b) => a.id.localeCompare(b.id))
      .map(({ id, currentBalance, creditApplied, journeyNode, paymentPlan: plan }) => [
        id,
        currentBalance,
        creditApplied,
        journeyNode,
        plan && [plan.externalPaymentPlanId, plan.status, plan.completedPayments],
      ]),
  ]);
}
